import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Building2 } from "lucide-react";
import { BRANCH } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import useBranchRole from "../../hooks/useBranchRole";

// Branch management CRUD screen (rendered as the Settings > Branch tab).
// Branch administration is org-admin-only (backend: only LoginUser.is_admin may
// create/edit/delete). Uses the same shared UI primitives as the other admin
// screens so the theme, fonts, weights and hover states are identical.
const Branch = () => {
    const { create, update } = useApiMethods();
    const { canManageBranches } = useBranchRole();

    const [branchData, setBranchData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    // Default-branch prompt: shown after creating the 2nd branch when existing
    // employees still have no branch. Admin picks which branch they go to.
    const [defaultBranchPrompt, setDefaultBranchPrompt] = useState({
        open: false, count: 0, branches: [], employees: [], selected: undefined,
    });

    const emptyForm = {
        id: null,
        name: "",
        mobile_number: "",
        email: "",
        city: "",
        state: "",
        address: "",
        landmark: "",
        pincode: "",
        country: "India",
        is_main_branch: false,
        is_active: true,
    };
    const [formData, setFormData] = useState(emptyForm);

    const resetForm = () => {
        setFormData(emptyForm);
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    const columns = [
        { accessor: "name", Header: "Branch Name", sortable: true, filterable: true },
        { accessor: "city", Header: "City", sortable: true, filterable: true },
        { accessor: "state", Header: "State", sortable: false, filterable: true },
        {
            accessor: "is_main_branch",
            Header: "Main Branch",
            sortable: false,
            Cell: ({ value }) =>
                value ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                        Main
                    </span>
                ) : (
                    <span className="text-gray-400 text-xs">—</span>
                ),
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(canManageBranches
            ? [
                  {
                      accessor: "action",
                      Header: "Actions",
                      sortable: false,
                      Cell: ({ row }) => {
                          const handleOpenDialog = () => {
                              setEditMode(true);
                              setEditId(row?.id);
                              setFormData({
                                  id: row?.id || null,
                                  name: row?.name || "",
                                  mobile_number: row?.mobile_number || "",
                                  email: row?.email || "",
                                  city: row?.city || "",
                                  state: row?.state || "",
                                  address: row?.address || "",
                                  landmark: row?.landmark || "",
                                  pincode: row?.pincode || "",
                                  country: row?.country || "India",
                                  is_main_branch: row?.is_main_branch ?? false,
                                  is_active: row?.is_active ?? true,
                              });
                              setOpenDialog(true);
                          };

                          return (
                              <div className="flex items-center gap-2">
                                  <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                      onClick={handleOpenDialog}
                                  >
                                      <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                      onClick={() => {
                                          setSelectedRow(row);
                                          setIsDeleteConfirmOpen(true);
                                      }}
                                  >
                                      <Trash className="h-4 w-4" />
                                  </Button>
                              </div>
                          );
                      },
                  },
              ]
            : []),
    ];

    const validate = () => {
        if (!formData.name?.trim()) {
            ToastMsg("error", "Branch name is required");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            setLoading(true);
            const payload = { ...formData };
            const response =
                editMode && editId
                    ? await update(BRANCH, payload)
                    : await create(BRANCH, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Branch ${editMode ? "updated" : "created"} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
                // 2nd branch created with employees still unassigned -> prompt the
                // admin to choose the default branch for them.
                if (response?.needs_default_branch_assignment) {
                    setDefaultBranchPrompt({
                        open: true,
                        count: response.unassigned_count || 0,
                        branches: Array.isArray(response.branches) ? response.branches : [],
                        employees: Array.isArray(response.employees) ? response.employees : [],
                        selected: undefined,
                    });
                }
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? "update" : "create"} branch`);
            }
        } catch (error) {
            // 403 is toasted globally by apiClient (backend enforces org-admin).
            console.error("Error saving branch:", error);
        } finally {
            setLoading(false);
        }
    };

    // Confirm: assign all currently-unassigned employees to the chosen branch.
    const handleAssignDefaultBranch = async () => {
        if (!defaultBranchPrompt.selected) {
            ToastMsg("error", "Please select a branch");
            return;
        }
        try {
            setLoading(true);
            const response = await create(BRANCH, {
                assign_default_branch: true,
                branch_id: defaultBranchPrompt.selected,
            });
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Employees assigned to the branch");
                setDefaultBranchPrompt({ open: false, count: 0, branches: [], employees: [], selected: undefined });
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to assign employees");
            }
        } catch (error) {
            console.error("Error assigning default branch:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedRow) return;
        try {
            setLoading(true);
            // Soft-delete convention used across the app: PUT with is_active=false.
            const payload = { ...selectedRow, is_active: false };
            const response = await update(BRANCH, payload, false, setLoading);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Branch deleted successfully");
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to delete branch");
            }
        } catch (error) {
            console.error("Error deleting branch:", error);
        } finally {
            setLoading(false);
            setSelectedRow(null);
            setIsDeleteConfirmOpen(false);
        }
    };

    const fieldClass = cn(
        "w-full rounded-lg transition-colors duration-200",
        "border-gray-300 dark:border-gray-600",
        "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
        "disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
    );
    const labelClass = "text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1";

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Building2}
                title={"Branches"}
                subtitle="Manage your organisation's branch offices"
                totalRecords={totalRecords}
                buttonEnable={canManageBranches}
                setOpenDialog={setOpenDialog}
                buttonName={"Add Branch"}
            />

            <Reactable
                columns={columns}
                data={branchData}
                setData={setBranchData}
                setTotalCount={setTotalRecords}
                url={BRANCH}
                serverSide={true}
                serverTrigger={serverTrigger}
                tableHeight="calc(100vh - 320px)"
                rowSize={canManageBranches ? "sm" : undefined}
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={false}
            />

            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={handleSave}
                title={editMode ? "Edit Branch" : "Add Branch"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                editMode={editMode}
                loading={loading}
                width="480px"
            >
                <form className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="name" className={labelClass}>
                            Branch Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter branch name"
                            required
                            disabled={loading}
                            className={fieldClass}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label htmlFor="mobile_number" className={labelClass}>Mobile Number</label>
                            <Input
                                id="mobile_number"
                                value={formData.mobile_number}
                                onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                                placeholder="Mobile number"
                                disabled={loading}
                                className={fieldClass}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="email" className={labelClass}>Email</label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="Email"
                                disabled={loading}
                                className={fieldClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label htmlFor="city" className={labelClass}>City</label>
                            <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="City" disabled={loading} className={fieldClass} />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="state" className={labelClass}>State</label>
                            <Input id="state" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="State" disabled={loading} className={fieldClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <label htmlFor="pincode" className={labelClass}>Pincode</label>
                            <Input id="pincode" value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} placeholder="Pincode" disabled={loading} className={fieldClass} />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="country" className={labelClass}>Country</label>
                            <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} placeholder="Country" disabled={loading} className={fieldClass} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="address" className={labelClass}>Address</label>
                        <Textarea
                            id="address"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Enter address"
                            rows={2}
                            disabled={loading}
                            className={fieldClass}
                        />
                    </div>

                    {/* Main branch toggle */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label htmlFor="is_main_branch" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            Main Branch
                        </label>
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, is_main_branch: !formData.is_main_branch })}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_main_branch ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600"
                            )}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_main_branch ? "translate-x-5" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </form>
            </DialogModal>

            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDelete}
                title="Delete Branch"
                type="danger"
                destructive={true}
                description={`Are you sure you want to delete "${selectedRow?.name || "this branch"}"? Employees in it will be unassigned, not deleted.`}
                confirmLabel="Delete"
                loading={loading}
            />

            {/* Default-branch assignment prompt (after 2nd branch created) —
                same confirm-style popup as delete, with a branch dropdown. */}
            <ConfirmPopupModal
                open={defaultBranchPrompt.open}
                onClose={() => setDefaultBranchPrompt({ open: false, count: 0, branches: [], employees: [], selected: undefined })}
                onConfirm={handleAssignDefaultBranch}
                title="Assign a Branch to Existing Employees"
                type="info"
                description={`${defaultBranchPrompt.count} employee(s) are not assigned to any branch. Choose which branch they should belong to.`}
                confirmLabel="Assign"
                cancelLabel="Skip for now"
                loading={loading}
                confirmDisabled={!defaultBranchPrompt.selected}
            >
                <div className="space-y-3">
                    {/* The employees that will be assigned. */}
                    {defaultBranchPrompt.employees.length > 0 && (
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                                Employees to be assigned ({defaultBranchPrompt.employees.length})
                            </label>
                            <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                                {defaultBranchPrompt.employees.map((e) => (
                                    <div key={e.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                                        <span className="text-gray-800 dark:text-gray-200 truncate">
                                            {`${e.first_name || ""} ${e.last_name || ""}`.trim() || e.employee_id || "-"}
                                        </span>
                                        {e.employee_id && <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{e.employee_id}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Assign all to branch <span className="text-red-500">*</span>
                        </label>
                        <select
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm px-3 py-2 text-gray-900 dark:text-gray-100"
                            value={defaultBranchPrompt.selected ?? ""}
                            onChange={(e) => setDefaultBranchPrompt((p) => ({ ...p, selected: e.target.value ? Number(e.target.value) : undefined }))}
                        >
                            <option value="">Select a branch</option>
                            {defaultBranchPrompt.branches.map((b) => (
                                <option key={b.id} value={b.id}>
                                    {b.name}{b.is_main_branch ? " (Main)" : ""}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                            You can move individual employees later from the employee list or via Branch Transfer.
                        </p>
                    </div>
                </div>
            </ConfirmPopupModal>
        </ContainerCard>
    );
};

export default Branch;
